<script setup lang="ts">
import { save } from "ionicons/icons";
import {
  IonInput,
  IonPage,
  IonButton,
  IonNote,
  IonLoading,
  IonFooter,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  onIonViewWillEnter,
  useIonRouter,
  IonAlert,
  isPlatform,
  getPlatforms,
} from "@ionic/vue";
import { computed, reactive } from "vue";
import { useWindowWidth } from "@/composition/window";
import { useMyAccount } from "../";
import { editPassword, editUser, requireDeleteUser } from "@/api/users";
import { useUserStore } from "@/stores/user";
import { useToasters } from "@/composition/toasters";
import { handleError } from "@/views";
import BeamianPlan from "@/views/_partials/BeamianPlan.vue";

const { isMobile } = useWindowWidth();
const { form } = useMyAccount();

const state = reactive({
  loading: false,
  errors: {
    name: [],
  },
  client_account: {},
  total_visitors: 0,
  alertDeleteAccount: false,
  plataform: "",
});

const toasters = useToasters();

const userStore = useUserStore();

const router = useIonRouter();

const emptyFieldsPresent = computed(
  () =>
    form.name === "" ||
    (form.new_password !== "" && form.confirm_password === form.new_password)
);

// const deleteUserPrompt = async () => {
//   const alert = await alertController.create({
//     header: "Are you sure?",
//     buttons: [
//       {
//         text: "Cancel",
//         role: "cancel",
//       },
//       {
//         text: "Delete",
//         role: "confirm",
//         handler: () => {
//           deleteUser(userStore.me)
//             .then(() => {
//               toasters.addSuccess(`Your account was deleted successfuly`);
//               router.push(`/onboarding/introduction`);
//               alert.remove();
//             })
//             .catch((err) => {
//               if (err.response.data.detail) {
//                 toasters.addError(err.response.data.detail);
//               } else {
//                 toasters.addError(
//                   `Failed to delete your account. Please try again later.`
//                 );
//               }
//               alert.remove();
//             });
//         },
//       },
//     ],
//   });

//   await alert.present();
// };

const deleteAccountButtons = [
  {
    text: "Cancel",
    role: "cancel",
    cssClass: "alert-button-cancel",
    handler: () => {
      state.alertDeleteAccount = false;
    },
  },
  {
    text: " OK ",
    role: "confirm",
    cssClass: "alert-button-confirm",
    handler: () => {
      state.alertDeleteAccount = false;
      // call delete request
      requireDeleteUser(userStore.me.id, { origin: state.plataform })
        .then(() => {
          toasters.addSuccess(
            "Request received. Your account will be deleted soon."
          );
          setTimeout(() => {
            userStore.setUserLogout();
            router.push("/login");
          }, 1500);
        })
        .catch((err) => {
          state.alertDeleteAccount = false;
          handleError(err);
        });
    },
  },
];

onIonViewWillEnter(() => {
  form.email = userStore.me.email || "";
  form.name = userStore.me.name || "";
  getContextPlatform();
});

const getContextPlatform = () => {
  if (isPlatform("ios")) {
    state.plataform = "iOS";
  }
  if (isPlatform("android")) {
    state.plataform = "Android";
  }
  if (!state.plataform && getPlatforms().includes("desktop")) {
    state.plataform = "desktop";
  }
};

const update = () => {
  state.loading = true;
  const user = {
    id: userStore.me.id,
    name: form.name,
    email: userStore.me.email,
  };
  editUser(user)
    .then(() => {
      state.loading = false;
      toasters.addSuccess("Your user data has been changed.");
    })
    .catch((err) => {
      state.loading = false;
      handleError(err);
    });

  if (form.password !== "") {
    editPassword(form.password)
      .then(() => {
        state.loading = false;
        toasters.addSuccess("Your password has been changed.");
      })
      .catch((err) => {
        state.loading = false;
        handleError(err);
      });
  }
};

const updateUser = (field: string, event: any) => {
  const updatedValue = event.detail.value;

  if (field === "name") {
    form.name = updatedValue;
  }
  if (field === "email") {
    form.email = updatedValue;
  }
};
</script>

<template>
  <ion-page>
    <ion-content v-if="isMobile">
      <section>
        <div class="container">
          <!-- <ion-avatar
            style="
              width: 7rem;
              height: 7rem;
              position: relative;
              margin: 0 auto;
            "
          >
            <img
              alt="User picture"
              src="https://s3.eu-west-1.amazonaws.com/f.beamian.com/v2/user-files/profile-pictures/46_9508e182c912391b8ce2ff31c0ef875d.jpeg"
            />
            <span
              class="icon"
              style="
                position: absolute;
                bottom: 0;
                right: 0;
                width: 2rem;
                height: 2rem;
                text-align: center;
                font-size: 1.25rem;
                padding: 0.28rem;
                background: #fff;
                border-radius: 100%;
              "
            >
              <ion-icon :icon="camera"></ion-icon>
            </span>
          </ion-avatar> -->

          <h1>Profile Overview</h1>
        </div>

        <ion-list>
          <ion-item>
            <ion-label position="stacked">Name</ion-label>
            <ion-input v-model="userStore.me.name" placeholder="John Doe"
              @ionChange="updateUser('name', $event)"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked"> Email</ion-label>
            <ion-input type="email" v-model="userStore.me.email" placeholder="john.doe@gmail.com" disabled>
            </ion-input>
          </ion-item>
        </ion-list>
        <div class="container">
          <h1>Change password</h1>
        </div>

        <ion-list>
          <ion-item>
            <ion-label position="stacked">Current password</ion-label>
            <ion-input type="password" v-model="form.current_password" placeholder="********">
            </ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">New password</ion-label>
            <ion-input type="password" v-model="form.new_password" placeholder="********">
            </ion-input>
          </ion-item>
          <ion-item :class="{
            'ion-invalid':
              form.new_password !== '' &&
              form.new_password !== form.confirm_password,
          }">
            <ion-label position="stacked"> Confirm password</ion-label>
            <ion-input type="password" v-model="form.confirm_password" placeholder="********">
            </ion-input>
            <ion-note slot="error"> Passwords do not match </ion-note>
          </ion-item>
        </ion-list>
        <div class="container">
          <h1>Your Plan</h1>
        </div>
        <beamian-plan />
        <ion-row class="ion-justify-content-center">
          <ion-button fill="outline" size="small" color="primary" @click="state.alertDeleteAccount = true">Delete
            Account</ion-button>
        </ion-row>
        <ion-alert :isOpen="state.alertDeleteAccount" :buttons="deleteAccountButtons" header="Are you sure?"
          message="This will permanently delete your account."></ion-alert>
      </section>
    </ion-content>

    <div class="colored-bg full-height" v-else>
      <div class="container">
        <div class="flex-desktop">
          <div class="full-width">
            <h1>Profile Overview</h1>
            <ion-list>
              <ion-item>
                <ion-label position="stacked">Name</ion-label>
                <ion-input v-model="userStore.me.name" placeholder="John Doe"
                  @ionChange="updateUser('name', $event)"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked"> Email</ion-label>
                <ion-input type="email" v-model="userStore.me.email" placeholder="john.doe@gmail.com" disabled>
                </ion-input>
              </ion-item>
            </ion-list>
          </div>
          <div class="full-width">
            <h1>Change password</h1>
            <ion-list>
              <ion-item>
                <ion-label position="stacked">Current password</ion-label>
                <ion-input type="password" v-model="form.current_password" placeholder="********">
                </ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">New password</ion-label>
                <ion-input type="password" v-model="form.new_password" placeholder="********">
                </ion-input>
              </ion-item>
              <ion-item :class="{
                'ion-invalid':
                  form.new_password !== '' &&
                  form.new_password !== form.confirm_password,
              }">
                <ion-label position="stacked"> Confirm password</ion-label>
                <ion-input type="password" v-model="form.confirm_password" placeholder="********">
                </ion-input>
                <ion-note slot="error"> Passwords do not match </ion-note>
              </ion-item>
            </ion-list>
          </div>
        </div>
        <div style="margin: 30px 80px">
          <h1>Your Plan</h1>
          <beamian-plan />
          <ion-row class="ion-justify-content-center ion-margin-top">
            <ion-button fill="outline" size="small" color="primary" @click="state.alertDeleteAccount = true">Delete
              Account</ion-button>
            <ion-alert :isOpen="state.alertDeleteAccount" :buttons="deleteAccountButtons" header="Are you sure?"
              message="This will permanently delete your account."></ion-alert>
          </ion-row>
        </div>
      </div>
    </div>

    <ion-footer v-if="isMobile">
      <div class="container">
        <section>
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-button expand="block" color="light" @clickp="router.back()">
                  <ion-icon slot="start" :icon="save"></ion-icon>
                  Cancel
                </ion-button>
              </ion-col>
              <ion-col>
                <ion-button expand="block" color="success" @click="update()" :disabled="emptyFieldsPresent">
                  <ion-icon slot="start" :icon="save"></ion-icon>
                  Save
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </section>
      </div>
    </ion-footer>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>
<style lang="scss" scoped>
.container .flex-desktop {
  justify-content: space-between;
}

.container .flex-desktop div {
  margin: 0 80px;
}

.billing_cicle_skeleton {
  height: 28px;
  margin-top: 10px;
}

.skeleton_limits {
  margin-bottom: 15px;
}
</style>
